// src/components/Map.js
import MapboxMap from './MapboxMap';
import useGetDetectors from '../../../hooks/useGetDetectors';
import useGetSoilSensors from '../../../hooks/useGetSoilSensors';
import useGetCameras from '../../../hooks/useGetCameras';
import DetectorMarker from '../Detector/DetectorMarker';
import SoilSensorMarker from '../SoilSensor/SoilSensorMarker';
import CameraMarker from '../Camera/CameraMarker';
import CannonStationMarker from '../Cannon/CannonStationMarker';
import useGetCannonStations from '../../../hooks/useGetCannonStations';
import CannonMarker from '../Cannon/CannonMarker';
import ErrorDialog from '../../Utils/ErrorDialog';
import { useState } from 'react';
import LoadingSpinner from '../../Utils/LoadingSpinner';
import FWIZonesLayer from '../FWI/FWIZonesLayer';
import useUserLocation from '../../../hooks/useGetUserLocation';
import UserMarker from '../../User/UserMarker';
import EmergencyCircuitsPerLayer from '../Emergency/EmergencyCircuitsPerLayer';
import useGetEmergencyCircuits from '../../../hooks/useGetEmergencyCircuits';
import MeetingPointsLayer from '../Emergency/MeetingPointsLayer';
import EducationBuildingsLayer from '../Emergency/EducationBuildingsLayer';
import ElderlyHomesLayer from '../Emergency/ElderlyHomesLayer';
import CriticalPopulationLayer from '../Emergency/CriticalPopulationLayer';
import RuralRoadsLayer from '../RuralRoads/RuralRoadsLayer';
import useGetRuralRoads from '../../../hooks/useGetRuralRoads';
import GatewayLayer from '../Gateway/GatewayLayer';
import useGetGatewayLinks from '../../../hooks/useGetGatewayLinks';
import { useRef, useEffect } from 'react';
import './Map.css';
import { getLargestPolygonArea } from '../../../helpers/coordinates';
import { rgbToHexFromStep } from '../../../helpers/simulation';


const Map = ( { can } ) => {
  const { detectors, setDetectors, isLoading: loadingDetectors, error: errorDetectors } = useGetDetectors();
  const { soilSensors, isLoading: loadingSoilSensors, error: errorSoilSensors } = useGetSoilSensors();
  const { cannonStations, isLoading: loadingCannonStations, error: errorCannonStations, zeusAvailable } = useGetCannonStations();
  const { cameras, isLoading: loadingCameras, error: errorCameras } = useGetCameras();

  

  const [showErrorDetectors, setShowErrorDetectors] = useState(true);
  const [showErrorSoilSensors, setShowErrorSoilSensors] = useState(true);
  const [showErrorCannonStations, setShowErrorCannonStations] = useState(true);

  const [mapRef, setMapRef] = useState(null);
  const [latestSimulation, setLatestSimulation] = useState(null);


  const [showDetectors, setShowDetectors] = useState(true);
  const [showSoilSensors, setShowSoilSensors] = useState(false);
  const [showCameras, setShowCameras] = useState(true);
  const [showCannonStations, setShowCannonStations] = useState(true);
  const [showCannons, setShowCannons] = useState(false);
  const [showFWIZones, setShowFWIZones] = useState(true);
  const [showUserLocation, setShowUserLocation] = useState(false);

  const [showGateways, setShowGateways] = useState(false);
  const [selectedGatewayIds, setSelectedGatewayIds] = useState(null);
  const [hoveredDetectorId, setHoveredDetectorId] = useState(null);
  const [showBatteries, setShowBatteries] = useState(false);
  const { gateways, links: gatewayLinks } = useGetGatewayLinks(showGateways && can(['admin']));

  useEffect(() => {
    if (gateways.length > 0) setSelectedGatewayIds(prev => prev === null ? new Set(gateways.map(g => g.gatewayId)) : prev);
  }, [gateways]);
  useEffect(() => {
    if (!showGateways) setHoveredDetectorId(null);
  }, [showGateways]);

  const [showLayersPanel, setShowLayersPanel] = useState(false);

  const [showEmergencyPanel, setShowEmergencyPanel] = useState(false);
  const [showMeetingPoints, setShowMeetingPoints] = useState(false);
  const [showEducationBuildings, setShowEducationBuildings] = useState(false);
  const [showElderlyHomes, setShowElderlyHomes] = useState(false);
  const [showCriticalPopulation, setShowCriticalPopulation] = useState(false);

  const [showRuralRoadsPanel, setShowRuralRoadsPanel] = useState(false);
  const [visibleRuralRoads, setVisibleRuralRoads] = useState(new Set());
  const { geojson: ruralRoadsGeojson, roads: ruralRoads } = useGetRuralRoads();

  const [showCircuitsPanel, setShowCircuitsPanel] = useState(false);
  const [visibleCircuits, setVisibleCircuits] = useState(new Set());
  const { geojson: circuitsGeojson, circuits } = useGetEmergencyCircuits();

  const [hoverZone, setHoverZone] = useState(null);

  const userLocation = useUserLocation(showUserLocation);

  const prevVariadorStatesRef = useRef({});
  const prevDetectorAlarmsRef = useRef({});
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'default') => {
    const id = Date.now();
    const duration = type === 'alarm' ? 15000 : 7000;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  };

  const playAlarmSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const lowFreq = 650;
      const highFreq = 1150;
      const sweepDur = 0.55;
      const cycles = 4;
      const totalDur = cycles * sweepDur * 2;

      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = 'sawtooth';

      const t = audioCtx.currentTime;
      osc.frequency.setValueAtTime(lowFreq, t);
      for (let i = 0; i < cycles; i++) {
        osc.frequency.linearRampToValueAtTime(highFreq, t + i * sweepDur * 2 + sweepDur);
        osc.frequency.linearRampToValueAtTime(lowFreq, t + (i + 1) * sweepDur * 2);
      }

      gain.gain.setValueAtTime(0.75, t);
      gain.gain.setValueAtTime(0.75, t + totalDur - 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, t + totalDur);

      osc.start(t);
      osc.stop(t + totalDur);
    } catch (e) {
      console.warn('No se pudo reproducir el sonido de alarma:', e);
    }
  };



  const riskLevels = [
    { label: 'Bajo', color: 'green', index: 1 },
    { label: 'Moderado', color: 'yellow', index: 2 },
    { label: 'Alto', color: 'orange', index: 3 },
    { label: 'Extremo', color: 'red', index: 4 }
  ];
  const getRiskLabel = idx => {
    const found = riskLevels.find(r => r.index === idx);
    return found ? found.label : 'N/D';
  };


  const isLoading = loadingDetectors || loadingSoilSensors || loadingCannonStations;

  const [activePopupId, setActivePopupId] = useState(null);

  const handleDeactivate = (detectorId) => {
    setDetectors(prev =>
      prev.map(det =>
        det.detectorId === detectorId
          ? { ...det, activeAlarm: false }
          : det
      )
    );
  };


  useEffect(() => {
    const prevStates = prevVariadorStatesRef.current;
    const currentStates = {};

    cannonStations.forEach(station => {
      const variadorChannel = station.channels.find(c => c.name === 'Variador');
      if (!variadorChannel) return;

      const stationId = station.stationId;
      const currentValue = variadorChannel.value;
      currentStates[stationId] = currentValue;

      const prevValue = prevStates[stationId];

      if (prevValue !== undefined && prevValue === 0 && currentValue === 1) {
        addToast(`Los cañones de la estación "${station.name}" se han encendido`);
      }
    });

    prevVariadorStatesRef.current = currentStates;
  }, [cannonStations]);

  useEffect(() => {
    const prevAlarms = prevDetectorAlarmsRef.current;
    const currentAlarms = {};

    detectors.forEach(detector => {
      const id = detector.detectorId;
      const isActive = !!detector.activeAlarm;
      currentAlarms[id] = isActive;

      if (prevAlarms[id] === false && isActive && can(['alarm-listener', 'admin'])) {
        playAlarmSound();
        addToast(`Alarma detectada: ${detector.location?.longName || id}`, 'alarm');
      }
    });

    prevDetectorAlarmsRef.current = currentAlarms;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectors]);



  return (

    
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {toasts.length > 0 && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 12, pointerEvents: 'none', width: '80vw', maxWidth: 900 }}>
          {toasts.map(t => (
            t.type === 'alarm' ? (
              <div key={t.id} style={{
                background: 'rgba(20, 10, 10, 0.93)', color: '#fff',
                padding: '28px 36px', borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                fontSize: 26, fontWeight: 700, fontFamily: "'Inter', sans-serif",
                cursor: 'pointer', pointerEvents: 'auto',
                borderLeft: '8px solid #e7b31a', textAlign: 'center',
                letterSpacing: '0.01em'
              }} onClick={() => setToasts(prev => prev.filter(t2 => t2.id !== t.id))}>
                <span className="alarm-toast-text">{t.message}</span>
              </div>
            ) : (
              <div key={t.id} style={{
                background: 'rgba(30, 30, 30, 0.85)', color: '#fff',
                padding: '12px 20px', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                fontSize: 14, fontFamily: "'Inter', sans-serif",
                cursor: 'pointer', pointerEvents: 'auto',
                borderLeft: '4px solid #e7b31a'
              }} onClick={() => setToasts(prev => prev.filter(t2 => t2.id !== t.id))}>
                {t.message}
              </div>
            )
          ))}
        </div>
      )}
      {/* Spinner centrado sobre el mapa */}
      {isLoading && <LoadingSpinner />}
    
    {/* Leyenda fija solo si Zonas FWI está activada */}
    
    {showFWIZones && can( ['risk-viewer','admin'] ) && (
      <div className='risk-legend'>
        {hoverZone ? (
          <>
            <div style={{ fontWeight: 600 }}>{hoverZone.zoneName}</div>
            <div><strong>Riesgo:</strong> {getRiskLabel(Number(hoverZone.riskIndex))}</div>
            <div><strong>FWI:</strong> {hoverZone.fwi}</div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Niveles de riesgo</div>
            {riskLevels.map(r => (
              <div key={r.index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <span style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  backgroundColor: r.color,
                  marginRight: 6,
                  border: '1px solid #ccc'
                }} />
                {r.label}
              </div>
            ))}
          </>
        )}
      </div>
    )}

    {/* Panel de control de capas */}
    {can(['map-layers-viewer','admin']) && (
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          background: "white",
          padding: "8px",
          borderRadius: "4px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          zIndex: 1
        }}
      >
        <div
          style={{ cursor: 'pointer', fontWeight: '700', marginBottom: showLayersPanel ? 6 : 0, userSelect: 'none' }}
          onClick={() => setShowLayersPanel(v => !v)}
        >
          {showLayersPanel ? '▾' : '▸'} Capas
        </div>
        {showLayersPanel && <div>
        <label>
          <input
            type="checkbox"
            checked={showDetectors}
            onChange={() => setShowDetectors(!showDetectors)}
          />
          Detectores
        </label>
        <br />
        <label>
          <input
            type="checkbox"
            checked={showFWIZones}
            onChange={() => setShowFWIZones(!showFWIZones)}
          />
          Zonas FWI
        </label>
        <br />
        <label>
          <input
            type="checkbox"
            checked={showCannonStations}
            onChange={() => setShowCannonStations(!showCannonStations)}
          />
          Depósitos
        </label>

        <br />
        <label>
          <input
            type="checkbox"
            checked={showCannons}
            onChange={() => setShowCannons(!showCannons)}
          />
          Cañones
        </label>
        <br />

        <label>
          <input
            type="checkbox"
            checked={showSoilSensors}
            onChange={() => setShowSoilSensors(!showSoilSensors)}
          />
          Sensores de suelo
        </label>
        <br />
        <label>
          <input
            type="checkbox"
            checked={showCameras}
            onChange={() => setShowCameras(!showCameras)}
          />
          Cámaras
        </label>
        <br />
        <label>
          <input
            type="checkbox"
            checked={showUserLocation}
            onChange={() => setShowUserLocation(!showUserLocation)}
          />
          Mi posición
        </label>
        {can(['admin']) && (
          <>
            <br />
            <label>
              <input
                type="checkbox"
                checked={showGateways}
                onChange={() => setShowGateways(!showGateways)}
              />
              Gateways LoRa
            </label>
            {showGateways && gateways.length > 0 && (
              <div style={{ paddingLeft: 12, marginTop: 4 }}>
                {gateways.map(gw => (
                  <label key={gw.gatewayId} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={selectedGatewayIds === null || selectedGatewayIds.has(gw.gatewayId)}
                      onChange={() => {
                        setSelectedGatewayIds(prev => {
                          const next = new Set(prev);
                          next.has(gw.gatewayId) ? next.delete(gw.gatewayId) : next.add(gw.gatewayId);
                          return next;
                        });
                      }}
                    />
                    <span style={{ fontSize: 12 }}>{gw.name}</span>
                  </label>
                ))}
              </div>
            )}
            <br />
            <label>
              <input
                type="checkbox"
                checked={showBatteries}
                onChange={() => setShowBatteries(v => !v)}
              />
              Modo batería
            </label>
          </>
        )}

        <br />
        <button
          onClick={() => {
            if (!mapRef) return;

            const map = mapRef;
            const sourceId = 'completed-simulation';

            // 🧹 Eliminar todas las capas asociadas a la fuente
            const layers = map.getStyle().layers
              .filter(layer => layer.source === sourceId)
              .map(layer => layer.id);

            layers.forEach(id => {
              if (map.getLayer(id)) map.removeLayer(id);
            });

            if (map.getSource(sourceId)) {
              map.removeSource(sourceId);
            }
            setLatestSimulation(null);
          }}
          style={{
            marginTop: '8px',
            marginBottom: '4px',
            fontSize: '16px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          Ocultar simulaciones
        </button>



        {/* Panel desplegable para circuitos de emergencia */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{ cursor: 'pointer', marginBottom: 4, marginLeft: 7, fontWeight: '600' }}
            onClick={() => setShowCircuitsPanel(!showCircuitsPanel)}
          >
            {showCircuitsPanel ? '-' : '+'} Circuitos de Emergencia
          </div>
          {showCircuitsPanel && (
            <div style={{ paddingLeft: 12 }}>
              {circuits.map(circuit => (
                <label key={circuit.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <input
                    type="checkbox"
                    checked={visibleCircuits.has(circuit.id)}
                    onChange={() => {
                      setVisibleCircuits(prev => {
                        const next = new Set(prev);
                        next.has(circuit.id) ? next.delete(circuit.id) : next.add(circuit.id);
                        return next;
                      });
                    }}
                  />
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: circuit.color, flexShrink: 0 }} />
                  {circuit.nombre}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Panel desplegable para caminos rurales */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{ cursor: 'pointer', marginBottom: 4, marginLeft: 7, fontWeight: '600' }}
            onClick={() => setShowRuralRoadsPanel(!showRuralRoadsPanel)}
          >
            {showRuralRoadsPanel ? '-' : '+'} Caminos Rurales
          </div>
          {showRuralRoadsPanel && (
            <div style={{ paddingLeft: 12 }}>
              {ruralRoads.map(road => (
                <label key={road.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <input
                    type="checkbox"
                    checked={visibleRuralRoads.has(road.id)}
                    onChange={() => {
                      setVisibleRuralRoads(prev => {
                        const next = new Set(prev);
                        next.has(road.id) ? next.delete(road.id) : next.add(road.id);
                        return next;
                      });
                    }}
                  />
                  {road.name}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Panel desplegable para capas de emergencia */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{ cursor: 'pointer', marginBottom: 4, marginLeft: 7, fontWeight: '600' }}
            onClick={() => setShowEmergencyPanel(!showEmergencyPanel)}
          >
            {showEmergencyPanel ? '-' : '+'} Emergencias
          </div>
          {showEmergencyPanel && (
            <div style={{ paddingLeft: 12 }}>
              <label>
                <input
                  type="checkbox"
                  checked={showMeetingPoints}
                  onChange={() => setShowMeetingPoints(!showMeetingPoints)}
                />
                Puntos de encuentro
              </label>
              <br />
              <label>
                <input
                  type="checkbox"
                  checked={showEducationBuildings}
                  onChange={() => setShowEducationBuildings(!showEducationBuildings)}
                />
                Centros de enseñanza
              </label>
              <br />
              <label>
                <input
                  type="checkbox"
                  checked={showElderlyHomes}
                  onChange={() => setShowElderlyHomes(!showElderlyHomes)}
                />
                Residencias de mayores
              </label>
              {can(['critical-population-layer', 'admin']) && (
                <>
                  <br />
                  <label>
                    <input
                      type="checkbox"
                      checked={showCriticalPopulation}
                      onChange={() => setShowCriticalPopulation(!showCriticalPopulation)}
                    />
                    Población crítica
                  </label>
                </>
              )}
            </div>
          )}
        </div>
        </div>}


      </div>
    )}


      {/* Mostrar error SOLO si falla la primera carga */}
      {errorDetectors && detectors.length === 0 && showErrorDetectors && (
        <ErrorDialog
          message="Error al cargar los detectores"
          onClose={() => setShowErrorDetectors(false)}
        />
      )}

      
      {errorSoilSensors && soilSensors.length === 0 && showErrorSoilSensors && (
        <ErrorDialog
          message="Error al cargar los sensores de suelo"
          onClose={() => setShowErrorSoilSensors(false)}
        />
      )}

      {errorCannonStations && cannonStations.length === 0 && showErrorCannonStations && (
        <ErrorDialog
          message="Error al cargar las estaciones de bombeo"
          onClose={() => setShowErrorCannonStations(false)}
        />
      )}

      

      <MapboxMap
        onFWIZoneHover={setHoverZone}
        onMapLoad={setMapRef}
        >

        {/* Marcador de ubicación del usuario si está disponible */}
        {userLocation && showUserLocation && (
          <UserMarker
            latitude={userLocation.latitude}
            longitude={userLocation.longitude}
          />
        )}
        {/* Marcadores cañones individuales */}
        {showCannons && !loadingCannonStations && !errorCannonStations && (cannonStations ?? []).flatMap(cannonStation =>
          (cannonStation.cannons ?? []).map(cannon => (
            <CannonMarker
              key={cannon.cannonId}
              cannon={cannon}
              isActive={activePopupId === `cannon-${cannon.cannonId}`}
              onActivate={() => setActivePopupId(`cannon-${cannon.cannonId}`)}
              onDeactivate={() => setActivePopupId(null)}
            />
          ))
        )}

        {can(['soil-sensor-viewer','admin']) && showSoilSensors && !loadingSoilSensors && (soilSensors ?? []).map(soilSensor => (
          <SoilSensorMarker
          key={soilSensor.soilSensorId} data={soilSensor}
          isActive={activePopupId === `soilsensor-${soilSensor.soilSensorId}`}
          onActivate={() => setActivePopupId(`soilsensor-${soilSensor.soilSensorId}`)}
          onDeactivate={() => setActivePopupId(null)}
          />
        ))}

        {showDetectors && (detectors ?? []).map(detector => (
          <DetectorMarker
          can={can}
          key={detector.detectorId}
          data={detector}
          onShowSimulation={(sim) => setLatestSimulation(sim)}
          isActive={activePopupId === `detector-${detector.detectorId}`}
          onActivate={() => setActivePopupId(`detector-${detector.detectorId}`)}
          onDeactivate={() => setActivePopupId(null)}
          onAlarmStopped={() => handleDeactivate(detector.detectorId)}
          onMouseEnter={showGateways && can(['admin']) ? () => setHoveredDetectorId(detector.detectorId) : undefined}
          onMouseLeave={showGateways && can(['admin']) ? () => setHoveredDetectorId(null) : undefined}
          showBatteries={showBatteries}
        />
        ))}

        

        {can(['cannon-viewer','admin']) && showCannonStations && !loadingCannonStations && (cannonStations ?? []).map(cannonStation => (
          <CannonStationMarker
            key={cannonStation.stationId}
            data={cannonStation}
            isActive={activePopupId === `cannonstation-${cannonStation.stationId}`}
            onActivate={() => setActivePopupId(`cannonstation-${cannonStation.stationId}`)}
            onDeactivate={() => setActivePopupId(null)}
            can={can}
            zeusAvailable={zeusAvailable}
            />
        ))}

        {can(['camera-viewer','admin']) && showCameras && !loadingCameras && !errorCameras && (cameras ?? []).map(camera => (
          <CameraMarker
          key={camera.name}
          data={camera}
          isActive={activePopupId === `camera-${camera.name}`}
          onActivate={() => setActivePopupId(`camera-${camera.name}`)}
          onDeactivate={() => setActivePopupId(null)}
        />
        ))}

        {latestSimulation?.steps && latestSimulation?.stepSpan && (
          <div className="map-legend simulation-legend-main-map">
            <div className="map-legend-steps">
              <ul>
                {Array.from({ length: latestSimulation.steps }, (_, i) => {
                  const step = i + 1;
                  const hours = step * latestSimulation.stepSpan;
                  const color = rgbToHexFromStep(step, latestSimulation.steps);
                  return (
                    <li key={step} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '12px',
                          height: '12px',
                          backgroundColor: color,
                          marginRight: '8px',
                          border: '1px solid #333'
                        }}
                      ></span>
                      {hours} horas
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              Área quemada después de {latestSimulation.steps * latestSimulation.stepSpan} horas<br />
              <span style={{ fontSize: 20 }}>
                <strong>{getLargestPolygonArea(latestSimulation.geojson).toLocaleString()} hectáreas</strong>
              </span>
            </div>
          </div>
        )}

        <RuralRoadsLayer geojson={ruralRoadsGeojson} visibleIds={visibleRuralRoads} />

        <EmergencyCircuitsPerLayer geojson={circuitsGeojson} visibleIds={visibleCircuits} />
        {showMeetingPoints && <MeetingPointsLayer visible={true} />}
        {showEducationBuildings && <EducationBuildingsLayer visible={true} />}
        {showElderlyHomes && <ElderlyHomesLayer visible={true} />}
        {can(['critical-population-layer', 'admin']) && showCriticalPopulation && <CriticalPopulationLayer visible={true} />}


        <FWIZonesLayer visible={showFWIZones} />

        {can(['admin']) && showGateways && (
          <GatewayLayer gateways={gateways} links={gatewayLinks} selectedGatewayIds={selectedGatewayIds} hoveredDetectorId={hoveredDetectorId} />
        )}

      </MapboxMap>
    </div>
  );
};

export default Map;
